import { useQuery } from '@tanstack/react-query';
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from '@tanstack/react-table';
import { useMemo, useState, type JSX } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiError } from '@/lib/api/error';
import { getSiteUsersRequest } from '@/services/sites';
import type { PaginatedResult } from '@/types/roles';
import type { SiteUserItem } from '@/types/sites';

/**
 * Live tab — fetches GET /api/sites/:id/users and renders the user list with
 * 20/page pagination. Phase 9 SITE-104.
 */
export interface SiteUsersTabProps {
  readonly siteId: number;
}

const PAGE_SIZE = 20;

export function SiteUsersTab({ siteId }: SiteUsersTabProps): JSX.Element {
  const [page, setPage] = useState<number>(1);

  const usersQuery = useQuery<PaginatedResult<SiteUserItem>, ApiError>({
    queryKey: ['site-users', siteId, page],
    queryFn: () => getSiteUsersRequest(siteId, { page, pageSize: PAGE_SIZE, status: 'all' }),
    enabled: Number.isFinite(siteId) && siteId > 0,
  });

  const columns = useMemo<ColumnDef<SiteUserItem>[]>(
    () => [
      { accessorKey: 'fullName', header: 'Full Name' },
      { accessorKey: 'email', header: 'Email' },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
        cell: ({ row }) => row.original.employeeId ?? '—',
      },
      {
        accessorKey: 'roleName',
        header: 'Role',
        cell: ({ row }) =>
          row.original.roleName !== null ? (
            <Badge variant="outline">{row.original.roleName}</Badge>
          ) : (
            '—'
          ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => {
          try {
            return new Date(row.original.createdAt).toLocaleDateString();
          } catch {
            return row.original.createdAt;
          }
        },
      },
    ],
    [],
  );

  const data = usersQuery.data?.items ?? [];
  const total = usersQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const tableData = data as unknown as SiteUserItem[];

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-3" data-testid="site-users-tab">
      {usersQuery.isLoading && (
        <p className="text-sm text-muted-foreground" data-testid="site-users-loading">
          Loading users…
        </p>
      )}
      {usersQuery.isError && (
        <p className="text-sm text-destructive" data-testid="site-users-error">
          Failed to load users.
        </p>
      )}
      {usersQuery.data && (
        <>
          <Table data-testid="site-users-table">
            <TableHeader>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((h) => (
                    <TableHead key={h.id}>
                      {h.isPlaceholder
                        ? null
                        : flexRender(h.column.columnDef.header, h.getContext())}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-8">
                    <span data-testid="site-users-empty">No users assigned to this site.</span>
                  </TableCell>
                </TableRow>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} data-testid={`site-users-row-${String(row.original.id)}`}>
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between pt-2">
            <p
              className="text-xs text-muted-foreground"
              data-testid="site-users-pagination-summary"
            >
              Page {page} of {pageCount} • {total} total
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                }}
                disabled={page <= 1}
                data-testid="site-users-pagination-prev"
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPage((p) => Math.min(pageCount, p + 1));
                }}
                disabled={page >= pageCount}
                data-testid="site-users-pagination-next"
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
